/**
 * Turning a joint's unique name into something that fits on screen.
 *
 * A joint is addressed as `instance/joint`, and both halves can be long:
 * the instance id is generated, and a module's own joint names are written
 * for a URDF rather than for a label floating next to an axis. Printed in
 * full they cover the robot they are annotating.
 *
 * The name still has to identify the joint, so this shortens rather than
 * abbreviates: a display name if the user set one, the joint half of the
 * unique name otherwise, and only then a middle truncation -- the head and
 * the tail are where the meaning is (`hip_yaw` vs `hip_pitch` differ at
 * the end, `left_` vs `right_` at the start).
 */

/** How many characters of a name a floating label carries before it is cut. */
export const DEFAULT_LABEL_MAX_CHARS = 18;

const ELLIPSIS = "…";

/**
 * The joint half of a `instance/joint` name, or the display name if set.
 *
 * The instance is dropped because the axis is already drawn on the module
 * it belongs to -- the panel and the tooltip still carry the full name.
 */
export function shortJointName(name: string, displayName?: string | null): string {
  if (displayName) {
    return displayName;
  }
  const withoutInstance = name.split("/").slice(1).join("/");
  return withoutInstance.length > 0 ? withoutInstance : name;
}

/** Cut the middle out of a name that is too long, keeping both ends. */
export function middleTruncate(text: string, maxChars: number): string {
  if (maxChars <= 1 || text.length <= maxChars) {
    return text;
  }
  const keep = maxChars - 1;
  const head = Math.ceil(keep / 2);
  const tail = keep - head;
  return tail > 0 ? `${text.slice(0, head)}${ELLIPSIS}${text.slice(-tail)}` : `${text.slice(0, head)}${ELLIPSIS}`;
}

/**
 * The label a floating axis annotation carries.
 *
 * Parameters
 * ----------
 * name
 *     The joint's unique `instance/joint` name.
 * displayName
 *     The name the user gave it, if any.
 * maxChars
 *     Length past which the middle is cut out.
 */
export function compactJointLabel(
  name: string,
  displayName?: string | null,
  maxChars = DEFAULT_LABEL_MAX_CHARS
): string {
  return middleTruncate(shortJointName(name, displayName), maxChars);
}


// ---------------------------------------------------------------------
// Decluttering: which of the labels that want to be drawn actually fit
// ---------------------------------------------------------------------

/** A label's claim on the screen, in pixels, with the anchor it belongs to. */
export interface LabelBox {
  name: string;
  /** Higher wins a collision. See `LABEL_PRIORITY`. */
  priority: number;
  /** Distance from the camera; the nearer label wins between equals. */
  distance: number;
  left: number;
  top: number;
  width: number;
  height: number;
  /** False for a label behind the camera or off the viewport. */
  onScreen: boolean;
}

/**
 * What each kind of label is worth when two of them overlap.
 *
 * A joint the audit flagged is the one thing the mode exists to show, so
 * it outranks the label the pointer happens to be near, which in turn
 * outranks the ones drawn because the user asked for all of them.
 */
export const LABEL_PRIORITY = {
  flagged: 3,
  hovered: 2,
  selected: 2,
  ordinary: 1,
} as const;

function overlaps(a: LabelBox, b: LabelBox): boolean {
  return (
    a.left < b.left + b.width
    && b.left < a.left + a.width
    && a.top < b.top + b.height
    && b.top < a.top + a.height
  );
}

/**
 * Choose the labels to draw so that none of them covers another.
 *
 * Walks them best-first -- priority, then nearest the camera -- and keeps
 * a label only if it clears everything already kept. Anything off screen
 * or behind the camera is dropped before that, since a hidden label
 * should not be able to suppress a visible one.
 *
 * Returns the names to draw.
 */
export function pickVisibleLabels(boxes: LabelBox[]): Set<string> {
  const ranked = boxes
    .filter((box) => box.onScreen)
    .sort((a, b) => (b.priority - a.priority) || (a.distance - b.distance));

  const kept: LabelBox[] = [];
  const names = new Set<string>();
  for (const box of ranked) {
    if (kept.some((other) => overlaps(box, other))) {
      continue;
    }
    kept.push(box);
    names.add(box.name);
  }
  return names;
}

